'use client';

import React, { useCallback } from 'react';
import { cn } from '@jucy-ui/common';
import { useController, useFormContext } from '@jucy-ui/forms';
import Typography from '@mui/material/Typography';
import { BranchSelect, BranchSelectProps } from '../../fields';
import { useShouldDisableBranch, useSortRegions } from '../hooks';
import { searchFormValuesToData } from '../lib';
import { jucySearchFormClasses } from '../lib/jucySearchFormClasses';
import { searchFormRepoStore } from '../store';
import { SearchFormValues } from '../types';

export interface SearchFormBranchSelectProps extends Omit<BranchSelectProps, 'name' | 'value' | 'onBlur' | 'onChange' | 'shouldDisableBranch'> {
    name: 'pickUpBranch' | 'dropOffBranch';
}

const findBranchBySiteCode = (siteCode: string | undefined) => searchFormRepoStore.branches.find((site) => site.siteCode === siteCode);

export const SearchFormBranchSelect = ({ name, className, ...props }: SearchFormBranchSelectProps) => {
    const {
        field: { onChange, ...field },
    } = useController({ name: name });
    const { getValues, setValue, getFieldState, clearErrors } = useFormContext<SearchFormValues>();
    const handleChange = useCallback(
        (e: Parameters<Required<BranchSelectProps>['onChange']>[0]) => {
            onChange(e);
            clearErrors(name);
            const branch = findBranchBySiteCode(e.target.value as string);
            if (!branch) {
                return;
            }

            const { driverAge, dropOffBranch, pickUpBranch } = searchFormValuesToData(getValues());
            if (name === 'pickUpBranch' && !driverAge && branch.minAges[0] && !getFieldState('driverAge').isTouched) {
                setValue('driverAge', `${branch.minAges[0]}`, { shouldTouch: false });
                clearErrors('driverAge');
            } else if (
                branch.minAges.length &&
                driverAge &&
                !branch.minAges.includes(driverAge) &&
                !branch.minAges.map(parseInt).every((age) => parseInt(driverAge) > age)
            ) {
                setValue('driverAge', `${branch.minAges[branch.minAges.length - 1]}`, { shouldTouch: false });
                clearErrors('driverAge');
            }

            const dropOffFieldState = getFieldState('dropOffBranch');
            if (!dropOffFieldState.isTouched && !dropOffBranch) {
                setValue('dropOffBranch', branch.siteCode, { shouldTouch: false });
                clearErrors('dropOffBranch');
            }

            if (name === 'dropOffBranch' && pickUpBranch && pickUpBranch.countryCode === branch.countryCode) {
                clearErrors('pickUpBranch');
            }
        },
        [clearErrors, getFieldState, getValues, name, onChange, setValue]
    );

    return (
        <BranchSelect
            shouldDisableBranch={useShouldDisableBranch(name === 'pickUpBranch' ? 'start' : 'end')}
            onChange={handleChange}
            sortRegion={useSortRegions()}
            className={cn(
                className,
                jucySearchFormClasses.branchSelect,
                name === 'pickUpBranch' ? jucySearchFormClasses.pickUpBranch : jucySearchFormClasses.dropOffBranch
            )}
            displayEmpty={Boolean(field.value)}
            renderValue={(value) => {
                const displayBranch = findBranchBySiteCode(!value && field.value ? field.value : value);

                return (
                    <Typography color={field.error ? 'error' : undefined} variant="inherit">
                        {`${displayBranch?.name}`}
                    </Typography>
                );
            }}
            {...field}
            {...props}
        />
    );
};
